import { useEffect, useRef, useState } from 'react'
import { Download, Upload, X, Check, AlertTriangle } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { BACKUP_KEYS } from './dataBackupKeys'
import { useAuth } from '@/auth/useAuth'
import {
  parseBackupKeys,
  needsUUIDRemap,
  remapLegacyIds,
  cleanOrphanReferences,
  writePayloadToLocalStorage,
  pushPayloadToBoundRepos,
} from './dataBackupRestore'

// ─── Sync metadata (test-only friction reducer for multi-device use) ────
//
// DebtOS is local-first by design — every browser / device has its own
// localStorage and there's no cloud sync. When the founder works on phone
// then opens the laptop, the laptop is silently stale. This metadata makes
// the staleness visible: "Last exported / imported" timestamps live in the
// DataBackup dropdown, and a small amber dot appears on the trigger button
// when the device's data hasn't been refreshed in over a week.
//
// Treated as a "test mode" feature — once cloud sync exists (post-SaaS
// migration), this will become redundant and can be removed cleanly.

const SYNC_META_KEY = 'debtos:syncMeta:v1'
/** Threshold past which the trigger button surfaces a soft amber dot. */
const STALE_DOT_THRESHOLD_DAYS = 7

interface SyncMeta {
  lastExportedAt?: string
  lastImportedAt?: string
}

function readSyncMeta(): SyncMeta {
  try {
    const raw = localStorage.getItem(SYNC_META_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as SyncMeta
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function writeSyncMeta(patch: Partial<SyncMeta>): void {
  try {
    const merged = { ...readSyncMeta(), ...patch }
    localStorage.setItem(SYNC_META_KEY, JSON.stringify(merged))
  } catch {
    // Storage unavailable / quota — non-fatal.
  }
}

/**
 * Render an ISO timestamp as "just now" / "5 minutes ago" / "3 days ago" /
 * "2 weeks ago". Returns null when given undefined so callers can distinguish
 * "never" from "long ago."
 */
function formatRelativeTime(iso: string | undefined): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const diffMs = Date.now() - then
  const sec = Math.round(diffMs / 1000)
  if (sec < 30) return 'just now'
  if (sec < 60) return `${sec} seconds ago`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`
  const wk = Math.round(day / 7)
  if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`
  const mo = Math.round(day / 30)
  return `${mo} month${mo === 1 ? '' : 's'} ago`
}

interface BackupData {
  version: string
  exportedAt: string
  keys: Record<string, unknown>
}

interface BackupPreview {
  debts: number
  incomeSources: number
  ledgerEntries: number
  recurringExpenses: number
  hasNotifications: boolean
  hasRecoveryAnchor: boolean
}

function summarize(keys: Record<string, unknown>): BackupPreview {
  const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
  // The recovery anchor is a singleton object, not an array — count it
  // by presence rather than length. Truthy object → has anchor.
  const anchor = keys['debtos:recoveryAnchor:v1']
  return {
    hasRecoveryAnchor: anchor !== undefined && anchor !== null,
    debts: arr(keys['debtos:debts:v1']).length,
    incomeSources: arr(keys['debtos:incomeSources:v1']).length,
    ledgerEntries: arr(keys['debtos:ledger:v1']).length,
    recurringExpenses: arr(keys['debtos:recurringExpenses:v1']).length,
    hasNotifications: !!keys['debtos:notifications:v1'],
  }
}

function readCurrentSnapshot(): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {}
  for (const key of BACKUP_KEYS) {
    const raw = localStorage.getItem(key)
    if (raw) {
      try {
        snapshot[key] = JSON.parse(raw)
      } catch {
        snapshot[key] = raw
      }
    }
  }
  return snapshot
}

/**
 * Data backup/restore dropdown. Safety net for users testing with real data.
 *
 * Export dumps all DebtOS localStorage keys into a timestamped JSON file.
 * Import parses a JSON file, shows a preview of what will be restored, and
 * on confirmation overwrites all keys then reloads.
 */
export function DataBackup() {
  const [open, setOpen] = useState(false)
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [importError, setImportError] = useState('')
  // After-action note: non-empty when cleanOrphanReferences dropped one or
  // more child rows during restore. Surfaces a small info banner alongside
  // the success state so the user knows what changed and isn't surprised
  // by missing history entries on the post-reload dashboard.
  const [importNote, setImportNote] = useState('')
  const [pendingImport, setPendingImport] = useState<{
    backup: BackupData
    preview: BackupPreview
    currentPreview: BackupPreview
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Restore-while-signed-in awareness. The backup file always overwrites
  // localStorage; for signed-in users we ALSO push the payload up to the
  // bound Supabase repos so cloud + local stay in sync and the user lands
  // on their restored data after reload without leaving authed mode.
  // v0.8.13 had a worse workaround — signing the user out — which left
  // real personal data sitting in localStorage where the same-browser
  // demo session could read it. v0.8.14 stays authed and writes to both.
  const { isAuthenticated } = useAuth()

  // Cached sync-meta for display. Reloaded on mount and whenever the dropdown
  // opens so the timestamps are fresh when the user actually looks.
  const [syncMeta, setSyncMeta] = useState<SyncMeta>(() => readSyncMeta())
  useEffect(() => {
    if (open) setSyncMeta(readSyncMeta())
  }, [open])

  const handleExport = () => {
    const snapshot = readCurrentSnapshot()
    const exportedAt = new Date().toISOString()
    const backup: BackupData = {
      version: '0.6.0',
      exportedAt,
      keys: snapshot,
    }

    const preview = summarize(snapshot)
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `debtos-backup-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
    setOpen(false)

    // Stamp the device's sync metadata so the dropdown reflects the fresh
    // export and the "stale" dot turns off (this device just produced a
    // canonical snapshot — it's the source of truth in this moment).
    writeSyncMeta({ lastExportedAt: exportedAt })
    setSyncMeta((prev) => ({ ...prev, lastExportedAt: exportedAt }))

    // Brief feedback via console so power users can verify
    console.info(
      `[DebtOS] Exported backup: ${preview.debts} debts, ${preview.incomeSources} income sources, ${preview.ledgerEntries} ledger entries`
    )
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const backup = JSON.parse(text) as BackupData

      if (!backup.keys || typeof backup.keys !== 'object') {
        throw new Error('Invalid backup file: missing keys')
      }

      const preview = summarize(backup.keys)
      const currentPreview = summarize(readCurrentSnapshot())
      setPendingImport({ backup, preview, currentPreview })
      setImportError('')
    } catch (err) {
      setImportStatus('error')
      setImportError(err instanceof Error ? err.message : 'Unknown error')
    }

    e.target.value = ''
  }

  const confirmRestore = async () => {
    if (!pendingImport) return
    const { backup } = pendingImport

    setImportError('')
    setImportNote('')

    try {
      // 1. Normalize the JSON's flat key-bag into typed entity arrays.
      let payload = parseBackupKeys(backup.keys as Record<string, unknown>)

      // 2. v0.6.0 backups used slug IDs ("debt-robinhood-cc"). The cloud
      //    schema requires uuid primary keys, so we mint fresh UUIDs and
      //    rewrite the ledger FK references before push. No-op for
      //    backups that already use UUIDs (v0.7.0+).
      if (needsUUIDRemap(payload)) {
        payload = remapLegacyIds(payload)
      }

      // 3. Drop ledger child rows that reference debts / income sources
      //    not present in the payload. localStorage tolerates orphan
      //    refs; the cloud schema's FK constraints reject them and the
      //    bulkReplace push 4xx's (v0.8.14 → v0.8.17 bugfix). Cleaning
      //    here keeps local and cloud consistent and surfaces the count
      //    to the user via importNote.
      const cleanResult = cleanOrphanReferences(payload)
      payload = cleanResult.payload

      // 4. Write to localStorage. Always. This is the data layer for
      //    anonymous users and the offline fallback for signed-in users.
      writePayloadToLocalStorage(payload)

      // 5. If signed in, push the payload UP to whichever repos are
      //    currently bound (Supabase for authed sessions). This keeps the
      //    user signed in and makes cloud + local consistent so the
      //    post-reload render shows the restored data either way.
      //    Throws on cloud failure — caught below, surfaced to the user
      //    with the underlying Supabase error message.
      if (isAuthenticated) {
        await pushPayloadToBoundRepos(payload)
      }

      writeSyncMeta({ lastImportedAt: new Date().toISOString() })

      // Build the after-action note if anything got cleaned.
      const droppedParts: string[] = []
      if (cleanResult.report.droppedAllocations > 0) {
        const n = cleanResult.report.droppedAllocations
        droppedParts.push(`${n} debt allocation${n === 1 ? '' : 's'}`)
      }
      if (cleanResult.report.droppedIncomeEntries > 0) {
        const n = cleanResult.report.droppedIncomeEntries
        droppedParts.push(`${n} income entr${n === 1 ? 'y' : 'ies'}`)
      }
      if (droppedParts.length > 0) {
        setImportNote(`Dropped ${droppedParts.join(' and ')} referencing deleted entities.`)
      }

      setPendingImport(null)
      setImportStatus('success')
      setTimeout(() => {
        window.location.reload()
      }, 800)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      console.warn('[debtos] Restore failed:', err)
      setImportStatus('error')
      setImportError(msg)
      setPendingImport(null)
    }
  }

  const cancelRestore = () => {
    setPendingImport(null)
  }

  const previewEmpty =
    pendingImport &&
    pendingImport.preview.debts === 0 &&
    pendingImport.preview.incomeSources === 0 &&
    pendingImport.preview.ledgerEntries === 0 &&
    pendingImport.preview.recurringExpenses === 0 &&
    !pendingImport.preview.hasRecoveryAnchor

  // Compute "stale" signal: this device hasn't been refreshed (imported into)
  // recently AND hasn't exported recently either. We use the larger of the
  // two timestamps as the "last sync activity" — a recent export is just as
  // valid a freshness signal as a recent import (it means this device IS
  // the source of truth right now). Conservative threshold (7 days) avoids
  // false alarms on the device the user is actively editing in-app.
  const lastSyncActivity = (() => {
    const exp = syncMeta.lastExportedAt
    const imp = syncMeta.lastImportedAt
    if (!exp && !imp) return null
    const expT = exp ? new Date(exp).getTime() : 0
    const impT = imp ? new Date(imp).getTime() : 0
    return Math.max(expT, impT)
  })()
  const isStale =
    lastSyncActivity === null ||
    Date.now() - lastSyncActivity > STALE_DOT_THRESHOLD_DAYS * 24 * 60 * 60 * 1000

  return (
    <>
      <div className="relative">
        <button
          onClick={() => {
            setOpen(!open)
            setImportStatus('idle')
          }}
          className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title={
            isStale ? "Backup & Restore — this device hasn't synced recently" : 'Backup & Restore'
          }
        >
          <Download className="w-5 h-5" />
          {isStale && (
            // Soft amber dot indicating this device hasn't seen export/import
            // activity in over a week. Not alarming — just a "you might want
            // to check" cue. Disappears on the next export or import.
            <span
              className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400/90 ring-2 ring-slate-900"
              aria-label="Data may be stale"
            />
          )}
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-72 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-slate-800">
              <h3 className="text-sm font-semibold text-white">Data Backup & Restore</h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-slate-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Sync-meta panel — surfaces the multi-device staleness reality.
                Shows when this specific device last produced or received a
                snapshot. "Never" surfaces in amber italics so it's visually
                distinguishable from a stale-but-recorded timestamp. */}
            <SyncMetaPanel meta={syncMeta} isStale={isStale} />

            <div className="p-3 space-y-2">
              <button
                onClick={handleExport}
                className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors text-left"
              >
                <Download className="w-5 h-5 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-white">Export Backup</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Download all data as JSON file
                  </div>
                </div>
              </button>

              <button
                onClick={handleImportClick}
                className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors text-left"
              >
                <Upload className="w-5 h-5 text-blue-400 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-white">Restore from Backup</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Upload a previously exported JSON
                  </div>
                </div>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileSelected}
                className="hidden"
              />

              {importStatus === 'success' && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 p-2 bg-emerald-950/40 border border-emerald-800/60 rounded text-xs text-emerald-300">
                    <Check className="w-4 h-4 shrink-0" />
                    <span>Restored successfully. Reloading...</span>
                  </div>
                  {importNote && (
                    <div className="flex items-start gap-2 p-2 bg-sky-950/30 border border-sky-800/60 rounded text-[11px] text-sky-200/90 leading-relaxed">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-sky-400" />
                      <span>{importNote}</span>
                    </div>
                  )}
                </div>
              )}

              {importStatus === 'error' && (
                <div className="flex items-center gap-2 p-2 bg-red-950/40 border border-red-800/60 rounded text-xs text-red-300">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>Import failed: {importError}</span>
                </div>
              )}
            </div>

            <div className="px-3 pb-3 pt-1">
              <p className="text-[10px] text-slate-600 leading-relaxed">
                Your data lives in browser localStorage. Export before testing with real data so you
                can restore if anything goes wrong.
              </p>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingImport}
        title={previewEmpty ? 'Backup file is empty' : 'Restore this backup?'}
        tone={previewEmpty ? 'warning' : 'danger'}
        confirmLabel={previewEmpty ? 'Restore anyway' : 'Overwrite with backup'}
        cancelLabel="Cancel"
        message={
          pendingImport ? (
            <div className="space-y-3">
              <p className="text-slate-400 text-xs">
                Exported{' '}
                <span className="text-slate-200">
                  {new Date(pendingImport.backup.exportedAt).toLocaleString()}
                </span>
              </p>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-800/50 border border-slate-700 rounded p-2">
                  <div className="text-slate-500 mb-1">Current data</div>
                  <ul className="space-y-0.5 text-slate-300">
                    <li>{pendingImport.currentPreview.debts} debts</li>
                    <li>{pendingImport.currentPreview.incomeSources} income sources</li>
                    <li>{pendingImport.currentPreview.ledgerEntries} months</li>
                    <li>{pendingImport.currentPreview.recurringExpenses} recurring expenses</li>
                    <li>
                      {pendingImport.currentPreview.hasRecoveryAnchor
                        ? '1 recovery anchor'
                        : 'no recovery anchor'}
                    </li>
                  </ul>
                </div>
                <div className="bg-emerald-950/30 border border-emerald-800/60 rounded p-2">
                  <div className="text-emerald-400 mb-1">Backup has</div>
                  <ul className="space-y-0.5 text-emerald-200">
                    <li>{pendingImport.preview.debts} debts</li>
                    <li>{pendingImport.preview.incomeSources} income sources</li>
                    <li>{pendingImport.preview.ledgerEntries} months</li>
                    <li>{pendingImport.preview.recurringExpenses} recurring expenses</li>
                    <li>
                      {pendingImport.preview.hasRecoveryAnchor
                        ? '1 recovery anchor'
                        : 'no recovery anchor'}
                    </li>
                  </ul>
                </div>
              </div>

              {previewEmpty && (
                <p className="text-amber-300 text-xs">
                  This backup has no debts, income sources, or months. Restoring will wipe your
                  current data and leave you with an empty app.
                </p>
              )}

              {isAuthenticated && !previewEmpty && (
                <div className="text-xs text-sky-200/90 bg-sky-500/10 border border-sky-500/30 rounded p-2.5 leading-relaxed">
                  <div className="font-semibold text-sky-300 mb-1">You&rsquo;re signed in</div>
                  Restore will overwrite both your cloud account and this device. You&rsquo;ll stay
                  signed in and the restored data will be there on every device after sync.
                </div>
              )}

              <p className="text-xs text-slate-500">
                Your current data will be overwritten. The page will reload after restore.
              </p>
            </div>
          ) : null
        }
        onConfirm={() => {
          void confirmRestore()
        }}
        onCancel={cancelRestore}
      />
    </>
  )
}

/**
 * Tiny panel surfacing the device-local sync metadata. Lives at the top of
 * the DataBackup dropdown so the user sees freshness state without a click.
 *
 * Three rendering states:
 *   - `isStale` (no recent activity): emphasized amber framing + a one-line
 *     prompt to remind the user this device is local-only.
 *   - Has activity within threshold: muted neutral framing, just the two
 *     "X ago" timestamps.
 *   - Either timestamp absent renders as "Never" in italics so it's clearly
 *     a "this hasn't happened" signal, not a missing-data bug.
 */
function SyncMetaPanel({ meta, isStale }: { meta: SyncMeta; isStale: boolean }) {
  const exportedAgo = formatRelativeTime(meta.lastExportedAt)
  const importedAgo = formatRelativeTime(meta.lastImportedAt)

  return (
    <div
      className={[
        'px-3 py-2.5 border-b border-slate-800 text-[11px]',
        isStale ? 'bg-amber-950/20' : 'bg-slate-950/40',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">
          This device
        </span>
        {isStale && <span className="text-amber-400/90 text-[10px]">may be stale</span>}
      </div>
      <div className="mt-1.5 space-y-0.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-slate-500">Last exported</span>
          <span
            className={
              exportedAgo ? 'text-slate-300 font-mono tabular-nums' : 'text-slate-600 italic'
            }
          >
            {exportedAgo ?? 'never'}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-slate-500">Last imported</span>
          <span
            className={
              importedAgo ? 'text-slate-300 font-mono tabular-nums' : 'text-slate-600 italic'
            }
          >
            {importedAgo ?? 'never'}
          </span>
        </div>
      </div>
      {isStale && (
        <p className="text-slate-500 mt-2 leading-snug">
          DebtOS is local to each browser. If you've been using another device, export there →
          import here to sync.
        </p>
      )}
    </div>
  )
}
